import { Box } from '@mui/material';
import { CloseOutlined } from '@mui/icons-material';
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    ThemeProvider,
    useTheme,
} from '@mui/material';
import { PaloThemeProvider, tokens } from '@synergy/theme-palo';
import { ReactElement, ReactNode } from 'react';

const c = tokens.color;

export interface PaloModalProps {
    isVisible: boolean;
    title?: ReactNode;
    /** Secondary header rendered below the title, under the ambient theme. */
    titleComponent?: ReactNode;
    handleClose?: () => void;
    actionChildren?: ReactNode;
    /** Space actions apart instead of right-aligning them. */
    actionChildrenCentered?: boolean;
    width?: 'xs' | 'sm' | 'md' | 'lg';
    /** Render the content under the PALO theme instead of the ambient theme. */
    themeContent?: boolean;
    children?: ReactNode;
}

/**
 * PALO-styled modal: compact left-aligned title, tight content and a bordered
 * action bar, with the PALO theme on its chrome. The content and actions render
 * under the app's ambient theme so legacy modal bodies keep working.
 */
export const PaloModal = ({
    isVisible,
    title,
    titleComponent,
    handleClose,
    actionChildren,
    actionChildrenCentered,
    width = 'sm',
    themeContent,
    children,
}: PaloModalProps): ReactElement => {
    const ambientTheme = useTheme();

    return (
        <PaloThemeProvider>
            <Dialog
                open={isVisible}
                onClose={handleClose}
                fullWidth
                maxWidth={width}
                PaperProps={{ sx: { borderRadius: '10px', boxShadow: tokens.shadow.s3 } }}
            >
                {(title || handleClose) && (
                    <DialogTitle
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            fontSize: '15px',
                            fontWeight: 640,
                            color: c.ink,
                            padding: '15px 20px 13px',
                            borderBottom: `1px solid ${c.line}`,
                        }}
                    >
                        <Box sx={{ flex: 1, minWidth: 0 }}>{title}</Box>
                        {handleClose && (
                            <IconButton
                                size="small"
                                aria-label="Schließen"
                                onClick={handleClose}
                                sx={{ color: c.ink3, mr: '-6px' }}
                            >
                                <CloseOutlined fontSize="small" />
                            </IconButton>
                        )}
                    </DialogTitle>
                )}
                {titleComponent && (
                    <ThemeProvider theme={ambientTheme}>
                        <Box sx={{ padding: '12px 20px', borderBottom: `1px solid ${c.line}` }}>{titleComponent}</Box>
                    </ThemeProvider>
                )}
                <DialogContent sx={{ padding: '18px 20px' }}>
                    {themeContent ? (
                        <Stack spacing={2.5} sx={{ pt: 1 }}>
                            {children}
                        </Stack>
                    ) : (
                        <ThemeProvider theme={ambientTheme}>
                            <Box sx={{ pt: 1 }}>{children}</Box>
                        </ThemeProvider>
                    )}
                </DialogContent>
                {actionChildren && (
                    <DialogActions
                        sx={{
                            padding: '12px 20px',
                            borderTop: `1px solid ${c.line}`,
                            justifyContent: actionChildrenCentered ? 'space-between' : 'flex-end',
                            gap: '8px',
                        }}
                    >
                        {actionChildren}
                    </DialogActions>
                )}
            </Dialog>
        </PaloThemeProvider>
    );
};
